import {
  SHAvatar,
  SHDivider,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { DateFormat, DateTimeFormat } from "@constants/format";
import { useUserPermissions } from "@hooks/userUserPermission";
import { RootState } from "@redux/store";
import { format } from "date-fns";
import { isEmpty } from "lodash";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";

export const SummarySupplierInformation = memo(() => {
  const { isSupplierBdmSale } = useUserPermissions();
  const storedDatabaseVersion = useSelector(
    (state: RootState) =>
      state.platformAnalysis.platformAnalysis?.databaseVersion,
  );
  const storedSummaryData = useSelector(
    (state: RootState) => state.platformAnalysis.platformAnalysis?.summary,
  );

  const {
    name,
    ownerAvatarUrl,
    ownerName,
    adviserName,
    practiceName,
    supplierName,
    lastModifiedDate,
    serial,
  } = useMemo(() => {
    const {
      name,
      supplierName,
      ownerAvatarUrl,
      ownerName,
      adviserName,
      practiceName,
      lastModifiedDate,
      serial,
    } = storedSummaryData || {};

    return {
      name: name || "",
      supplierName: supplierName || "",
      ownerAvatarUrl: ownerAvatarUrl || undefined,
      ownerName: ownerName || "",
      adviserName: adviserName || "",
      practiceName: practiceName || "",
      lastModifiedDate: lastModifiedDate || new Date(),
      serial: serial || "",
    };
  }, [storedSummaryData]);

  const isShowAdviserName = useMemo(() => {
    return isSupplierBdmSale && !isEmpty(adviserName) && !isEmpty(practiceName);
  }, [adviserName, isSupplierBdmSale, practiceName]);

  return (
    <SHStack
      direction={{ xs: "column", lg: "row" }}
      justifyContent={{ xs: "flex-start", lg: "space-between" }}
      alignItems={{ xs: "flex-start", lg: "stretch" }}
      gap={"25px"}
    >
      <SHStack spacing={"25px"} sx={{ width: { xs: "100%", lg: "50%" } }}>
        <SHTypography variant="subtitle1" fontSize={"24px"}>
          {name}
        </SHTypography>
        <SHStack direction={"row"} spacing="5px">
          <SHAvatar
            width={"24px"}
            height={"24px"}
            src={ownerAvatarUrl || undefined}
          />
          <SHTypography variant="body2" colorVariant="third">
            {ownerName}
          </SHTypography>
        </SHStack>
        <SHStack direction={"row"} alignItems={"center"} spacing="15px">
          <SHTypography
            variant="body2"
            colorVariant="third"
          >{`Last edit: ${format(
            lastModifiedDate,
            DateTimeFormat,
          )}`}</SHTypography>
          <SHDivider orientation="vertical" sx={{ height: "14px" }} />
          <SHTypography variant="body2" colorVariant="third">
            {storedDatabaseVersion
              ? `Data valid: ${format(
                  new Date(storedDatabaseVersion),
                  DateFormat,
                )}`
              : ""}
          </SHTypography>
          <SHDivider orientation="vertical" sx={{ height: "14px" }} />
          <SHTypography
            variant="body2"
            colorVariant="third"
          >{`Serial: ${serial}`}</SHTypography>
        </SHStack>
      </SHStack>
      <SHStack spacing={"25px"} sx={{ width: { xs: "100%", lg: "50%" } }}>
        {isShowAdviserName ? (
          <SHStack spacing={"5px"}>
            <SHTypography variant="subtitle1">
              This analysis is for
            </SHTypography>
            <SHStack direction={"row"} spacing={"4px"}>
              <SHTypography variant="subtitle2">{adviserName}</SHTypography>
              <SHTypography variant="body2" colorVariant="third">
                from
              </SHTypography>
              <SHTypography variant="subtitle2">{practiceName}</SHTypography>
            </SHStack>
          </SHStack>
        ) : (
          <SHStack spacing={"5px"}>
            <SHTypography variant="subtitle1">
              Not for dissemination
            </SHTypography>
            <SHStack direction={"row"} spacing={"2px"}>
              <SHTypography variant="body2" colorVariant="third">
                This analysis is created for
              </SHTypography>
              <SHTypography variant="body1" colorVariant="third">
                {supplierName}
              </SHTypography>
              <SHTypography variant="body2" colorVariant="third">
                internal use only.
              </SHTypography>
            </SHStack>
          </SHStack>
        )}
      </SHStack>
    </SHStack>
  );
});
